import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PieChart as PieIcon } from 'lucide-react';

const formatCurrency = (value) => {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div style={{
      background: 'var(--surface-secondary)',
      border: '1px solid var(--border-primary)',
      borderRadius: '0.5rem',
      padding: '0.875rem',
      boxShadow: 'var(--shadow-elevation-2)',
      minWidth: '200px'
    }}>
      <div style={{
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: '0.5rem',
        fontSize: '0.875rem'
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', fontSize: '0.8125rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Projects:</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{data.projectCount}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Revenue:</span>
          <span style={{ color: 'var(--interactive-primary)', fontWeight: 500 }}>
            {formatCurrency(data.revenue)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Labor Cost:</span>
          <span style={{ color: 'var(--status-warning)', fontWeight: 500 }}>
            {formatCurrency(data.laborCost)}
          </span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          borderTop: '1px solid var(--border-primary)',
          paddingTop: '0.375rem',
          marginTop: '0.25rem'
        }}>
          <span style={{ color: 'var(--text-secondary)' }}>Profit:</span>
          <span style={{
            color: data.profit >= 0 ? 'var(--status-success)' : 'var(--status-error)',
            fontWeight: 600
          }}>
            {formatCurrency(data.profit)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Avg Margin:</span>
          <span style={{
            color: data.avgMarginPercent >= 50 ? 'var(--status-success)' :
                   data.avgMarginPercent >= 20 ? 'var(--status-warning)' : 'var(--status-error)',
            fontWeight: 500
          }}>
            {data.avgMarginPercent.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

const TemplateProfitChart = ({ data, chartColors }) => {
  // Sort by profit descending and take top 15
  const chartData = [...data]
    .sort((a, b) => b.profit - a.profit)
    .slice(0, 15)
    .map(item => ({
      ...item,
      name: item.templateType.length > 25
        ? item.templateType.substring(0, 22) + '...'
        : item.templateType
    }));

  if (data.length === 0) {
    return (
      <div className="chart-section">
        <div className="chart-header">
          <div>
            <h3 className="chart-title">Profitability by Template Type</h3>
            <p className="chart-subtitle">Revenue and costs split evenly across project categories</p>
          </div>
        </div>
        <div className="empty-state">
          <PieIcon size={48} />
          <h3>No template data available</h3>
          <p>Add employee rates to see profitability analysis.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-section">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Profitability by Template Type</h3>
          <p className="chart-subtitle">
            Revenue and costs split evenly across project categories (top 15)
          </p>
        </div>
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.borderPrimary}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              tickFormatter={formatCurrency}
              tick={{ fill: chartColors.textSecondary, fontSize: 12 }}
              axisLine={{ stroke: chartColors.borderPrimary }}
              tickLine={{ stroke: chartColors.borderPrimary }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: chartColors.textPrimary, fontSize: 12 }}
              axisLine={{ stroke: chartColors.borderPrimary }}
              tickLine={false}
              width={140}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '1rem' }}
              formatter={(value) => (
                <span style={{ color: chartColors.textSecondary, fontSize: '0.875rem' }}>
                  {value}
                </span>
              )}
            />
            <Bar
              dataKey="revenue"
              name="Revenue"
              fill="#3B82F6"
              radius={[0, 4, 4, 0]}
            />
            <Bar
              dataKey="laborCost"
              name="Labor Cost"
              fill="#F59E0B"
              radius={[0, 4, 4, 0]}
            />
            <Bar
              dataKey="profit"
              name="Profit"
              fill="#10B981"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TemplateProfitChart;
